import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuthentication } from '@/features/auth/AuthenticationContext'
import { useToast } from '@/hooks/use-toast'
import { getAppConfig } from '@/lib/app-config'

export function SetupPage() {
    const isMockAuth = getAppConfig().mockAuth
    const [isSettingUp, setIsSettingUp] = useState(false)
    const navigate = useNavigate()
    const { toast } = useToast()
    const { setup, mockSetup, isLoading } = useAuthentication()

    const handleMockSetup = async (token: string) => {
        setIsSettingUp(true)
        try {
            await mockSetup(token)
            toast({
                title: 'Setup Complete!',
                description: `You are now the administrator (${token === 'admin@dev' ? 'Dev Admin' : 'Dev User'}).`,
            })
            navigate('/')
        } catch (error: any) {
            toast({
                title: 'Setup failed',
                description:
                    error.response?.data?.detail ||
                    error.response?.data?.error ||
                    error.message ||
                    'An error occurred during setup',
                variant: 'destructive',
            })
        } finally {
            setIsSettingUp(false)
        }
    }

    const handleSetup = () => {
        setIsSettingUp(true)
        setup() // Redirects to Microsoft — page will reload on return
    }

    const loading = isLoading || isSettingUp

    return (
        <div className="bg-white p-8 rounded-lg shadow-md">
            <div className="text-center mb-8">
                <div className="mx-auto w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                    <ShieldCheck className="h-8 w-8 text-indigo-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                    Welcome — First-Time Setup
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                    {isMockAuth
                        ? 'Development mode — sign in to become the first administrator'
                        : 'Sign in with your Microsoft account to set up as the first administrator'}
                </p>
                <p className="mt-1 text-xs text-gray-400">
                    This will only appear once. The first user becomes the system administrator.
                </p>
            </div>

            {isMockAuth ? (
                <div className="space-y-3">
                    <Button
                        onClick={() => handleMockSetup('admin@dev')}
                        className="w-full flex items-center justify-center gap-3 py-6 bg-indigo-600 hover:bg-indigo-700"
                        disabled={loading}
                        size="lg"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="h-5 w-5 animate-spin" />
                                Setting up…
                            </>
                        ) : (
                            <>🛡️ Set Up as Dev Admin</>
                        )}
                    </Button>
                    <Button
                        onClick={() => handleMockSetup('user@dev')}
                        className="w-full flex items-center justify-center gap-3 py-6 bg-slate-600 hover:bg-slate-700"
                        disabled={loading}
                        size="lg"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="h-5 w-5 animate-spin" />
                                Setting up…
                            </>
                        ) : (
                            <>👤 Set Up as Dev User</>
                        )}
                    </Button>
                </div>
            ) : (
                <Button
                    onClick={handleSetup}
                    className="w-full flex items-center justify-center gap-3 py-6"
                    disabled={loading}
                    size="lg"
                >
                    {loading ? (
                        <>
                            <Loader2 className="h-5 w-5 animate-spin" />
                            Setting up…
                        </>
                    ) : (
                        <>
                            <svg className="h-5 w-5" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                            </svg>
                            Set Up with Microsoft
                        </>
                    )}
                </Button>
            )}

            <p className="mt-6 text-xs text-center text-gray-400">
                {isMockAuth
                    ? '⚠️ Mock authentication — do not use in production'
                    : 'Protected by Azure Active Directory'}
            </p>
        </div>
    )
}
